import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { GitHubIcon, GoogleIcon, DiscordIcon } from '../components/OAuthIcons'
import { oauthConfig } from '../lib/oauth-config'
import { getRedirectDestination } from '../utils/authHelpers'
import LogoFiverFlow from '../assets/LogoFiverFlow.png'
import IconVert from '../assets/Icon - Vert.png'

const AuthSignIn: React.FC = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Redirect after successful login
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        const redirectTo = getRedirectDestination() || '/dashboard'
        navigate(redirectTo, { replace: true })
      }
    }
    checkAuth()
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        const redirectTo = getRedirectDestination() || '/dashboard'
        navigate(redirectTo, { replace: true })
      }
    })
    
    return () => subscription.unsubscribe()
  }, [navigate])

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        setError(error.message)
      }
      // Navigation will be handled by useEffect listening to auth state change
    } catch (err: any) {
      setError(err.message)
      setLoading(false)
    }
  }

  const handleSocialLogin = async (provider: 'github' | 'google' | 'discord') => {
    setLoading(true)
    setError('')
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          ...oauthConfig.options,
          // S'assurer que la redirection pointe vers notre domaine Fiverflow
          redirectTo: oauthConfig.redirectTo,
          // Personnaliser le nom de l'application (si supporté par le provider)
          queryParams: {
            ...oauthConfig.queryParams,
            // Pour GitHub: utiliser le nom de l'application configuré dans Supabase
            // Pour Google: le nom vient de la configuration OAuth dans Google Cloud Console
            // Pour Discord: le nom vient de la configuration de l'application Discord
            // Note: Le nom "Fiverflow" doit être configuré dans le dashboard Supabase
            // Authentication > Settings > Application Name
          }
        }
      })
      if (error) setError(error.message)
      // Note: signInWithOAuth redirige automatiquement, donc on ne set pas loading à false ici
      // car le composant sera démonté lors de la redirection
    } catch (err: any) {
      setError(err.message)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen h-screen w-full bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 overflow-hidden">
      <div className="flex h-full flex-col lg:flex-row">
        {/* Left: form */}
        <div className="flex flex-1 items-center justify-center px-4 py-6 sm:px-8">
          <div className="w-full max-w-md space-y-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="flex items-center gap-3">
                <img src={IconVert} alt="FiverFlow Icon" className="h-8 w-auto" />
                <div className="hidden sm:block h-6 w-px bg-gray-200 dark:bg-slate-700" />
                <img src={LogoFiverFlow} alt="FiverFlow" className="h-6 w-auto" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">Welcome back</h1>
                <p className="text-sm text-gray-600 dark:text-slate-400">Sign in to your account</p>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => handleSocialLogin('github')}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-sm"
              >
                <GitHubIcon className="w-4 h-4 mr-2 text-gray-900 dark:text-slate-100" />
                <span className="truncate">Continue with GitHub</span>
              </button>
              <button
                onClick={() => handleSocialLogin('google')}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-sm"
              >
                <GoogleIcon className="w-4 h-4 mr-2" />
                <span className="truncate">Continue with Google</span>
              </button>
              <button
                onClick={() => handleSocialLogin('discord')}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-200 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 text-sm"
              >
                <DiscordIcon className="w-4 h-4 mr-2 text-indigo-400" />
                <span className="truncate">Continue with Discord</span>
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wide text-gray-600 dark:text-slate-400">
                <span className="px-3 bg-white dark:bg-slate-900">or email</span>
              </div>
            </div>

            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-3 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2]"
                  placeholder="Enter your email"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-3 text-sm border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2]"
                  placeholder="Enter your password"
                />
              </div>
              {error && <div className="text-rose-400 text-sm px-1">{error}</div>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#9c68f2] hover:bg-[#8655e6] text-white font-medium rounded-xl transition-colors disabled:opacity-50 text-sm"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="text-center text-sm text-gray-600 dark:text-slate-400">
              Don't have an account?{' '}
              <button onClick={() => navigate('/register')} className="text-[#9c68f2] hover:text-[#8655e6] font-medium">Sign Up</button>
            </div>
          </div>
        </div>

        {/* Right: branding */}
        <div className="hidden lg:flex flex-1 items-center justify-center bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700">
          <div className="max-w-md px-10 space-y-6 text-center">
            <h2 className="text-2xl font-semibold">Welcome to FiverFlow</h2>
            <p className="text-gray-600 dark:text-slate-400">Build, manage and scale your workflow with a modern, fast and secure platform.</p>
            <div className="grid gap-4">
              {['Instant navigation','Advanced analytics','Secure by design'].map((t) => (
                <div key={t} className="rounded-xl border border-gray-200 dark:border-slate-700 p-4 text-sm text-gray-600 dark:text-slate-400">
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthSignIn


